import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import Logo from './Logo';
import { NAVIGATION, LINKS } from '../config/siteContent';

/**
 * Header - light glassmorphic top bar with React Router integration.
 *
 * - Sticky, compresses on scroll (80px → 64px) and adds shadow
 * - Desktop: hover-intent dropdowns for items with `children`
 * - Mobile: collapsible accordion menu, auto-closes on route change
 * - Active route gets a subtle accent treatment via NavLink's `aria-current`
 *
 * Nav order (verified against reference site Header.html):
 *   About Us → Solutions → Case Studies → Resources → Compare → Contact Us
 */
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-close mobile menu when the route changes
  useEffect(() => {
    setMobileOpen(false);
    setOpenMobileSection(null);
    setOpenDropdown(null);
  }, [location.pathname]);

  const handleEnter = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 120);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/85 backdrop-blur-xl border-b border-border-subtle shadow-soft'
          : 'bg-white/60 backdrop-blur-md border-b border-transparent'
      }`}
    >
      <nav
        className={`max-w-[1440px] mx-auto px-6 lg:px-8 flex items-center justify-between transition-all duration-300 ${
          scrolled ? 'h-16' : 'h-20'
        }`}
      >
        <Link to="/" aria-label="iConnect home" className="flex-shrink-0">
          <Logo height={scrolled ? 42 : 54} tagline="EDI in the cloud" />
        </Link>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-1">
          {NAVIGATION.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const isOpen = openDropdown === item.label;
            return (
              <li
                key={item.label}
                className="relative"
                onMouseEnter={() => hasChildren && handleEnter(item.label)}
                onMouseLeave={() => hasChildren && handleLeave()}
              >
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-accent-primary bg-accent-tint'
                        : 'text-text-secondary hover:text-accent-primary hover:bg-accent-tint'
                    }`
                  }
                >
                  {item.label}
                  {hasChildren && (
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </NavLink>

                {hasChildren && (
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                        className="absolute left-0 top-full pt-2 min-w-[240px]"
                      >
                        <div className="rounded-xl bg-white border border-border-subtle shadow-card-hover overflow-hidden">
                          <ul className="py-2">
                            {item.children.map((child, idx) => (
                              <motion.li
                                key={child.label}
                                initial={{ opacity: 0, x: -4 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.025 }}
                              >
                                <Link
                                  to={child.to}
                                  className="block px-4 py-2.5 text-sm text-text-secondary hover:text-accent-primary hover:bg-accent-tint transition-colors"
                                >
                                  {child.label}
                                </Link>
                              </motion.li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </li>
            );
          })}
        </ul>

        <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
          <a
            href={LINKS.iExchangeWebLogin}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors font-medium"
          >
            Login
          </a>
          <Link to="/contact" className="btn-primary text-sm py-3 px-5">
            Talk to an Expert
          </Link>
        </div>

        {/* Mobile menu trigger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden p-2 -mr-2 text-text-primary"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="lg:hidden bg-white border-t border-border-subtle overflow-hidden"
          >
            <ul className="px-6 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
              {NAVIGATION.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isOpen = openMobileSection === item.label;
                return (
                  <li key={item.label}>
                    {hasChildren ? (
                      <>
                        <button
                          onClick={() => setOpenMobileSection(isOpen ? null : item.label)}
                          className="w-full flex items-center justify-between py-3 text-text-primary font-medium"
                        >
                          {item.label}
                          <ChevronDown
                            size={16}
                            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                        <AnimatePresence>
                          {isOpen && (
                            <motion.ul
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden pl-4"
                            >
                              {item.children.map((child) => (
                                <li key={child.label}>
                                  <Link
                                    to={child.to}
                                    className="block py-2 text-sm text-text-secondary hover:text-accent-primary"
                                  >
                                    {child.label}
                                  </Link>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={item.to}
                        className="block py-3 text-text-primary font-medium"
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                );
              })}
              <li className="pt-3 border-t border-border-subtle mt-2">
                <a
                  href={LINKS.iExchangeWebLogin}
                  className="block py-2 text-text-secondary"
                >
                  Login
                </a>
                <Link
                  to="/contact"
                  className="btn-primary w-full justify-center mt-3"
                >
                  Talk to an Expert
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
